/**
 * Journal Entry Detail Page (v3 layout, BE-16 data)
 *
 * Shows the full double-entry breakdown: each line with Account, Debit, Credit.
 * Industry standard (QuickBooks/Xero/Zoho): always a separate detail page, never inline.
 * A manual entry not yet reversed can be reversed by a TC admin (with a reason); an entry and its reversal link to
 * each other.
 *
 * Route: /accounting/journal-entries/:id
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton,
  Alert, Divider, Link, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, useTheme,
} from '@mui/material';
import { ArrowBack, Undo } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { sourceInfo } from '../../utils/accounting/journalSources';
import { journalEntryService } from '../../services/accounting/journalEntry.service';
import { serverMessage } from '../../utils/apiConvert';
import WalletService from '../../services/wallet.service';

const JournalEntryDetail = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntry(await journalEntryService.getById(id));
    } catch (err) {
      setError(serverMessage(err, 'Journal entry not found'));
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleReverse = async () => {
    setReversing(true);
    setError(null);
    try {
      // a transfer is undone from its own endpoint (refused once the money was used); a manual journal is reversed here
      if (isTransfer) await WalletService.reverseTransfer(entry.sourceId, reason.trim());
      else await journalEntryService.reverse(id, reason.trim());
      setReverseOpen(false);
      setReason('');
      setSnackbar(isTransfer ? 'Transfer undone' : 'Entry reversed');
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to reverse the entry'));
      setReverseOpen(false);
    } finally {
      setReversing(false);
    }
  };

  if (loading && !entry) {
    return <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} /></Box>;
  }

  if (!entry) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{error || 'Journal entry not found.'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/accounting/journal-entries')} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Journal Entries
        </Button>
      </Box>
    );
  }

  const source = sourceInfo(entry.sourceType);
  const lines = entry.lines || [];
  const totalDebits = Number(entry.totalDebits || 0);
  const totalCredits = Number(entry.totalCredits || 0);
  const balanced = Math.abs(totalDebits - totalCredits) < 0.005;
  const hasVatBase = lines.some((l) => l.vatBase !== null && l.vatBase !== undefined);
  const isTransfer = entry.sourceType === 'BANK_TRANSFER';
  const canReverse = currentUser?.role === 'TC_ADMIN' && (entry.sourceType === 'MANUAL' || isTransfer) && entry.status === 'POSTED' && !entry.reversedById;

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ maxWidth: 900 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ textTransform: 'none', fontSize: fs.sm }}>
            Back
          </Button>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold, wordBreak: 'break-all' }}>
            {entry.entryNumber}
          </Typography>
          <Chip label={source.label} size="small" sx={{ bgcolor: source.bg, color: '#fff', fontSize: '0.65rem', height: 22 }} />
          <Chip label={entry.status} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: entry.status === 'POSTED' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: entry.status === 'POSTED' ? '#22c55e' : '#f59e0b' }} />
          {entry.reversedById && <Chip label="Reversed" size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444' }} />}
          <Box sx={{ flex: 1 }} />
          {canReverse && (
            <Button size="small" variant="outlined" color="error" startIcon={<Undo />} onClick={() => setReverseOpen(true)} sx={{ textTransform: 'none' }}>
              {isTransfer ? 'Undo transfer' : 'Reverse'}
            </Button>
          )}
        </Box>

        {/* Entry Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Date</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>{formatDate(entry.entryDate)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Posted By</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>{entry.postedBy?.name || 'System'}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Posted At</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>{entry.postedAt ? formatDate(entry.postedAt) : '—'}</Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Description</Typography>
          <Typography sx={{ fontSize: fs.sm }}>{entry.description}</Typography>
          {entry.counterpartyName && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>Counterparty: {entry.counterpartyName}</Typography>
          )}
          {(entry.reversalOfId || entry.reversedById) && (
            <Typography sx={{ fontSize: fs.xs, mt: 0.5 }}>
              {entry.reversalOfId && (
                <>Reverses <Link component={RouterLink} to={`/accounting/journal-entries/${entry.reversalOfId}`}>the original entry</Link></>
              )}
              {entry.reversedById && (
                <>Reversed by <Link component={RouterLink} to={`/accounting/journal-entries/${entry.reversedById}`}>its reversal entry</Link></>
              )}
            </Typography>
          )}
        </Paper>

        {/* Dr/Cr Line Items */}
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={getBrandedTableHeadSx(theme)}>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold, width: '45%' }}>Account</TableCell>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold, width: '25%' }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontSize: fs.xs, fontWeight: fw.semibold, width: '15%' }}>Debit</TableCell>
                  <TableCell align="right" sx={{ fontSize: fs.xs, fontWeight: fw.semibold, width: '15%' }}>Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.lineNumber}>
                    <TableCell sx={{ fontSize: fs.xs, py: 0.75 }}>
                      {line.account ? `${line.account.number} — ${line.account.name}` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: fs.xs, py: 0.75, color: 'text.secondary' }}>
                      {line.description || ''}
                      {hasVatBase && line.vatBase !== null && line.vatBase !== undefined && (
                        <Box component="span" sx={{ display: 'block' }}>VAT base {formatCurrency(line.vatBase)}</Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: fs.xs, py: 0.75, fontWeight: Number(line.debit) > 0 ? fw.medium : fw.regular }}>
                      {Number(line.debit) > 0 ? formatCurrency(line.debit) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: fs.xs, py: 0.75, fontWeight: Number(line.credit) > 0 ? fw.medium : fw.regular }}>
                      {Number(line.credit) > 0 ? formatCurrency(line.credit) : ''}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold, py: 0.75 }}>Total</TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontSize: fs.xs, fontWeight: fw.semibold, py: 0.75 }}>
                    {formatCurrency(totalDebits)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: fs.xs, fontWeight: fw.semibold, py: 0.75 }}>
                    {formatCurrency(totalCredits)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Balance check */}
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: balanced ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: 1 }}>
          <Typography sx={{ fontSize: fs.xs, color: balanced ? '#22c55e' : '#ef4444', fontWeight: fw.medium }}>
            {balanced ? 'Entry is balanced' : 'Entry is NOT balanced -- debits != credits'}
          </Typography>
        </Box>
      </Box>

      {/* Reverse dialog */}
      <Dialog open={reverseOpen} onClose={() => setReverseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md }}>{isTransfer ? 'Undo this transfer?' : `Reverse ${entry.entryNumber}?`}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm, mb: 1.5 }}>
            Posts the opposite entry, dated today. The original stays in the books, linked to its reversal.
          </Typography>
          {isTransfer && (
            <Alert severity="info" sx={{ mb: 1.5, fontSize: fs.xs }}>
              Undo only a transfer recorded by mistake. If the money really moved and came back, record a transfer back instead.
            </Alert>
          )}
          <TextField label="Reason" size="small" fullWidth autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Posted twice" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReverseOpen(false)} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" size="small" disabled={reversing || !reason.trim()} onClick={handleReverse} sx={{ textTransform: 'none' }}>
            {reversing ? 'Reversing...' : 'Reverse Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default JournalEntryDetail;
